import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import {
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  getNextSchedulePeriod,
  getSchedulePeriods,
  isScheduleDayFull,
  SCHEDULE_DAYS,
  validateSchedulePeriods,
} from "./schedulePeriods";

const EMPTY_DAYS = [];
const EMPTY_DAY_TIMES = {};

const toPickerTime = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(":").map(Number);
  return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
};

const ScheduleTimesWidget = ({
  formData = {},
  formContext,
  onChange,
}) => {
  const [copiedPeriod, setCopiedPeriod] = useState(null);
  const [overrideDismissed, setOverrideDismissed] = useState(false);
  const days = formData.days || EMPTY_DAYS;
  const dayTimes = formData.dayTimes || EMPTY_DAY_TIMES;
  const selectedScheduleType =
    formContext?.formData?.scheduleVariableFlexible?.scheduleType || "";
  const showOverrideCard =
    !overrideDismissed &&
    ["24/7 Round-the-Clock Care", "Live-in Care"].includes(
      selectedScheduleType,
    );
  const timeErrors = useMemo(
    () => validateSchedulePeriods(formData),
    [formData],
  );
  const errorsByPeriod = useMemo(
    () =>
      timeErrors.reduce((result, error) => {
        result[`${error.day}-${error.index}`] = error.message;
        return result;
      }, {}),
    [timeErrors],
  );
  const daysWithErrors = useMemo(
    () => new Set(timeErrors.map((error) => error.day).filter(Boolean)),
    [timeErrors],
  );

  useEffect(() => {
    const missingDays = days.filter((day) => !dayTimes[day]);
    if (missingDays.length === 0) return;

    const updatedDayTimes = { ...dayTimes };
    missingDays.forEach((day) => {
      const periods = getSchedulePeriods();
      updatedDayTimes[day] = {
        timePeriods: periods,
      };
    });
    onChange({ ...formData, dayTimes: updatedDayTimes });
  }, [days, dayTimes, formData, onChange]);

  const updateDay = (day, periods) => {
    onChange({
      ...formData,
      dayTimes: {
        ...dayTimes,
        [day]: { timePeriods: periods },
      },
    });
  };

  const setPeriodValue = (day, index, field, value) => {
    const periods = getSchedulePeriods(dayTimes[day]);
    periods[index] = { ...periods[index], [field]: value };
    updateDay(day, periods);
  };

  const toggleDay = (day, checked) => {
    const updatedDays = checked
      ? [...days, day]
      : days.filter((selectedDay) => selectedDay !== day);
    const updatedDayTimes = { ...dayTimes };

    if (checked && !updatedDayTimes[day]) {
      const [firstPeriod] = getSchedulePeriods();
      updatedDayTimes[day] = {
        timePeriods: [firstPeriod],
      };
    }

    onChange({
      ...formData,
      days: SCHEDULE_DAYS.filter((scheduleDay) =>
        updatedDays.includes(scheduleDay),
      ),
      dayTimes: updatedDayTimes,
    });
  };

  const addPeriod = (day) => {
    if (isScheduleDayFull(dayTimes[day]) || daysWithErrors.has(day)) return;
    const periods = getSchedulePeriods(dayTimes[day]);
    updateDay(day, [...periods, getNextSchedulePeriod(dayTimes[day])]);
  };

  const removePeriod = (day, index) => {
    const periods = getSchedulePeriods(dayTimes[day]);
    if (periods.length <= 1) return;
    updateDay(
      day,
      periods.filter((_, periodIndex) => periodIndex !== index),
    );
  };

  useEffect(() => {
    setOverrideDismissed(false);
    setCopiedPeriod(null);
  }, [selectedScheduleType]);

  return (
    <Box>
      {showOverrideCard && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#ffebee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2">
            {selectedScheduleType === "24/7 Round-the-Clock Care"
              ? "Full-time caregiver with rotating caregivers providing 24-hour coverage"
              : "Caregiver lives in the home, providing extended care with daily rest periods"}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOverrideDismissed(true)}
          >
            Change
          </Button>
        </Paper>
      )}

      <Typography variant="h6" color="default.main" mb={1}>
        Select Days
      </Typography>

      <Box display="flex" flexWrap="wrap" columnGap={2} rowGap={0.5}>
        {SCHEDULE_DAYS.map((day) => (
          <FormControlLabel
            key={day}
            control={
              <Checkbox
                checked={days.includes(day)}
                onChange={(event) => toggleDay(day, event.target.checked)}
              />
            }
            label={day}
          />
        ))}
      </Box>

      {!showOverrideCard && days.length > 0 && (
          <Grid2 container spacing={3} mt={1}>
            {SCHEDULE_DAYS.filter((day) => days.includes(day)).map((day) => {
              const periods = getSchedulePeriods(dayTimes[day]);
              return (
                <Grid2 item size={{ xs: 12, md: 6 }} key={day}>
                <Typography variant="h6" mb={1}>
                  {day}
                </Typography>

                {periods.map((period, index) => {
                  const key = `${day}-${index}`;
                  const message = errorsByPeriod[key];
                  const showError = Boolean(message);
                  const startTime = toPickerTime(period.startTime);

                  return (
                    <Box key={key} mb={2}>
                      <Typography variant="body2" mb={0.75}>
                        Time Period {index + 1}
                      </Typography>
                      <Box sx={{ overflowX: "auto", pt: 1, pb: 0.5 }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "minmax(130px, 1fr) minmax(130px, 1fr) auto",
                            gap: 1,
                            alignItems: "center",
                            minWidth: { xs: 500, sm: 0 },
                          }}
                        >
                          <TimePicker
                            label="Start Time"
                            value={startTime}
                            ampm
                            timeSteps={{ minutes: 1 }}
                            onChange={(event) =>
                              setPeriodValue(
                                day,
                                index,
                                "startTime",
                                event?.isValid() ? event.format("HH:mm") : "",
                              )
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                error: showError,
                                sx: {
                                  "& .MuiInputBase-input": {
                                    paddingRight: 0,     // stops the text being cut off
                                    paddingLeft: "12px",
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiInputAdornment-root": {
                                    marginLeft: 0,       // default is 8px
                                  },
                                },
                              },
                            }}
                          />
                          <TimePicker
                            label="End Time"
                            value={toPickerTime(period.endTime)}
                            ampm
                            timeSteps={{ minutes: 1 }}
                            onChange={(event) =>
                              setPeriodValue(
                                day,
                                index,
                                "endTime",
                                event?.isValid() ? event.format("HH:mm") : "",
                              )
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                error: showError,
                                sx: {
                                  "& .MuiInputBase-input": {
                                    paddingRight: 0,     // stops the text being cut off
                                    paddingLeft: "12px",
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiInputAdornment-root": {
                                    marginLeft: 0,       // default is 8px
                                  },
                                },

                              },
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              size="small"
                              color="primary"
                              aria-label={`Copy ${day} time period ${index + 1}`}
                              onClick={() => setCopiedPeriod({ ...period })}
                              sx={{
                                minWidth: 48,
                                px: 0.5,
                                display: "flex",
                                flexDirection: "column",
                                lineHeight: 1.2,
                              }}
                            >
                              <CopyIcon fontSize="small" />
                              Copy
                            </Button>
                            <Button
                              size="small"
                              aria-label={`Paste into ${day} time period ${
                                index + 1
                              }`}
                              disabled={!copiedPeriod}
                              onClick={() => {
                                if (!copiedPeriod) return;
                                const updatedPeriods = getSchedulePeriods(
                                  dayTimes[day],
                                );
                                updatedPeriods[index] = { ...copiedPeriod };
                                updateDay(day, updatedPeriods);
                              }}
                              sx={{
                                minWidth: 48,
                                px: 0.5,
                                display: "flex",
                                flexDirection: "column",
                                lineHeight: 1.2,
                              }}
                            >
                              <PasteIcon fontSize="small" />
                              Paste
                            </Button>
                            {periods.length > 1 && (
                              <Button
                                size="small"
                                color="primary"
                                aria-label={`Delete ${day} time period ${
                                  index + 1
                                }`}
                                onClick={() => removePeriod(day, index)}
                                sx={{
                                  minWidth: 48,
                                  px: 0.5,
                                  display: "flex",
                                  flexDirection: "column",
                                  lineHeight: 1.2,
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                                Delete
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      {showError && (
                        <FormHelperText error role="alert">
                          {message}
                        </FormHelperText>
                      )}
                    </Box>
                  );
                })}

                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addPeriod(day)}
                  variant="contained"
                  disabled={
                    isScheduleDayFull(dayTimes[day]) || daysWithErrors.has(day)
                  }
                >
                  Add Another Time Period
                </Button>
                </Grid2>
              );
            })}
          </Grid2>
        )}
    </Box>
  );
};

export default ScheduleTimesWidget;
